import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ============================================
// Types pour les Relevés de Devoirs
// ============================================

export interface ReleveDevoirs {
  id: number;
  code_releve: string;
  matiere: number;
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  option: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  format_releve?: string;
  fichier_pdf_genere?: string;
  fichier_scan?: string;
  status: 'genere' | 'imprime' | 'rempli' | 'scanne' | 'traite' | 'verifie' | 'valide';
  nombre_etudiants: number;
  nombre_pages: number;
  genere_par?: number;
  genere_par_nom?: string;
  date_generation: string;
  date_impression?: string;
  date_scan?: string;
  scanne_par?: number;
  scanne_par_nom?: string;
  date_validation?: string;
  valide_par?: number;
  valide_par_nom?: string;
  total_notes_detectees: number;
  notes_confiance_haute: number;
  notes_a_verifier: number;
  lignes_a_verifier: number;
  enseignant_nom?: string;
}

export interface LigneReleveDevoirsOCR {
  id: number;
  releve: number;
  numero_ligne: number;
  numero_page: number;
  etudiant?: number;
  etudiant_matricule?: string;
  etudiant_nom?: string;
  matricule_detecte: string;
  confiance_matricule: number;
  nom_detecte?: string;        // Texte OCR de la zone "Nom et Prénoms"
  methode_detection?: string;  // 'pyzbar' | 'cv_chars' | 'cv_corrige' | 'cv_nom_fuzzy' | ''
  note_d1_detectee?: number;
  confiance_d1: number;
  note_d1_validee?: number;
  absent_d1: boolean;
  note_d2_detectee?: number;
  confiance_d2: number;
  note_d2_validee?: number;
  absent_d2: boolean;
  image_base64: string;
  necessite_verification: boolean;
  verifie: boolean;
  verifie_par?: number;
  date_verification?: string;
  commentaire: string;
  erreur: string;
  confiance_globale: number;
}

export interface ReleveDevoirsDetail extends ReleveDevoirs {
  lignes_ocr: LigneReleveDevoirsOCR[];
}

// ============================================
// Types pour les Relevés de Correction
// ============================================

export interface ReleveCorrection {
  id: number;
  code_releve: string;
  matiere: number | {
    id: number;
    code_matiere: string;
    nom_matiere: string;
  };
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  option: string;
  classes?: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  type_examen: 'normal' | 'rattrapage';
  format_releve?: string;
  fichier_pdf_genere?: string;
  fichier_scan?: string;
  status: 'genere' | 'imprime' | 'rempli' | 'scanne' | 'traite' | 'verifie' | 'valide';
  nombre_copies: number;
  nombre_pages: number;
  genere_par?: number;
  genere_par_nom?: string;
  enseignant_nom?: string;
  date_generation: string;
  date_scan?: string;
  scanne_par?: number;
  scanne_par_nom?: string;
  date_validation?: string;
  valide_par?: number;
  valide_par_nom?: string;
  total_notes_detectees: number;
  notes_confiance_haute: number;
  notes_a_verifier: number;
  copies_manquantes: number;
  lignes_a_verifier: number;
}

export interface LigneReleveCorrectionOCR {
  id: number;
  releve: number;
  numero_ligne: number;
  numero_page: number;
  copie_examen?: number;
  copie_code?: string;
  etudiant_matricule?: string;
  etudiant_nom?: string;
  code_copie_detecte: string;
  confiance_code: number;
  note_cases_detectee?: number;
  confiance_cases: number;
  cases_detectees?: { entier?: number; decimal?: number };
  note_manuscrite_detectee?: number;
  confiance_manuscrit: number;
  note_finale?: number;
  source_note: 'cases' | 'manuscrit' | 'croise' | 'manuel' | 'incertain';
  confiance_finale: number;
  absent_detecte: boolean;
  absent_valide: boolean;
  image_base64: string;
  necessite_verification: boolean;
  raison_verification: string;
  verifie: boolean;
  verifie_par?: number;
  date_verification?: string;
  commentaire: string;
  erreur: string;
}

export interface ReleveCorrectionDetail extends ReleveCorrection {
  lignes_ocr: LigneReleveCorrectionOCR[];
}

// ============================================
// Types pour les Traitements et Configuration
// ============================================

export interface TraitementOCR {
  id: number;
  type_traitement: 'devoirs' | 'correction';
  releve_devoirs?: number;
  releve_correction?: number;
  releve_code?: string;
  fichier_source: string;
  status: 'en_cours' | 'termine' | 'erreur' | 'annule';
  progression: number;
  nombre_pages_traitees: number;
  nombre_lignes_detectees: number;
  nombre_notes_detectees: number;
  nombre_erreurs: number;
  confiance_moyenne: number;
  date_debut: string;
  date_fin?: string;
  duree_secondes?: number;
  lance_par?: number;
  lance_par_nom?: string;
  message_erreur: string;
  celery_task_id: string;
}

export interface ConfigurationOCR {
  id: number;
  nom: string;
  actif: boolean;
  seuil_confiance_matricule: number;
  seuil_confiance_note: number;
  seuil_verification: number;
  tesseract_lang: string;
  tesseract_psm: number;
  tesseract_oem: number;
  appliquer_binarisation: boolean;
  appliquer_debruitage: boolean;
  appliquer_rotation: boolean;
  resolution_dpi: number;
  tolerance_case_cochee: number;
  date_creation: string;
  date_modification: string;
}

export interface StatistiquesOCR {
  total_releves_devoirs: number;
  total_releves_correction: number;
  releves_en_attente: number;
  releves_traites: number;
  releves_a_verifier: number;
  releves_valides: number;
  total_notes_traitees: number;
  confiance_moyenne: number;
  taux_verification_manuelle: number;
  // Legacy fields (rétrocompatibilité)
  total_copies?: number;
  copies_traitees?: number;
  copies_en_attente?: number;
  taux_reussite?: number;
}

// ============================================
// Payloads
// ============================================

export interface GenererReleveDevoirsPayload {
  matiere_id: number;
  niveau: 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
  option: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  enseignant_nom?: string;
  enseignant_prenoms?: string;
  date_evaluation?: string;
  format?: 'cases_separees' | 'zone_guidee' | 'simple';
}

export interface GenererBatchClasseSpec {
  niveau: string;
  option: string;
  semestre: string;
}

export interface GenererBatchDevoirsPayload {
  classes: GenererBatchClasseSpec[];
  annee_academique: string;
  format?: 'cases_separees' | 'zone_guidee' | 'simple';
  date_evaluation?: string;
}

export interface GenererBatchResultItem {
  code_releve: string;
  matiere_code: string;
  matiere_nom: string;
  classe: string;
  nombre_etudiants: number;
  nombre_pages: number;
}

export interface GenererBatchDevoirsResponse {
  success: boolean;
  total_generes: number;
  total_erreurs: number;
  resultats: GenererBatchResultItem[];
  erreurs: string[];
}

export interface GenererReleveCorrectionPayload {
  matiere_id: number;
  niveau: 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
  option: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  type_examen: 'normal' | 'rattrapage';
  enseignant_nom?: string;
  enseignant_prenoms?: string;
}

// ============================================
// Types pour Batch Correction
// ============================================

export interface MatiereAvecCopies {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  copies_associees: number;
  enseignant: {
    id: number;
    nom: string;
    prenoms: string;
    nom_complet: string;
  } | null;
  releve_existant: {
    id: number;
    code_releve: string;
    status: string;
  } | null;
}

export interface MatiereAvecCopiesParams {
  niveau: string;
  option: string;
  semestre: string;
  annee_academique: string;
  type_examen?: string;
}

export interface GenererBatchCorrectionPayload {
  matiere_ids: number[];
  niveau: string;
  option: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  type_examen: 'normal' | 'rattrapage';
  formateurs: Record<string, { nom: string; prenoms: string }>;
}

export interface GenererBatchCorrectionResultItem {
  releve_id: number;
  code_releve: string;
  matiere_code: string;
  matiere_nom: string;
  nombre_copies: number;
  nombre_pages: number;
  enseignant: string;
}

export interface GenererBatchCorrectionResponse {
  success: boolean;
  total_generes: number;
  total_erreurs: number;
  resultats: GenererBatchCorrectionResultItem[];
  erreurs: string[];
}

export interface ValiderLigneDevoirsPayload {
  ligne_id: number;
  note_d1?: number;
  absent_d1?: boolean;
  note_d2?: number;
  absent_d2?: boolean;
  etudiant_id?: number;
  commentaire?: string;
}

export interface ValiderLigneCorrectionPayload {
  ligne_id: number;
  note_finale?: number;
  absent?: boolean;
  commentaire?: string;
}

export interface GenererReleveResponse {
  success: boolean;
  releve_id: number;
  code_releve: string;
  fichier_pdf?: string;
  nombre_etudiants?: number;
  nombre_copies?: number;
  nombre_pages: number;
}

export interface SaisieRapideNote {
  etudiant_id: number;
  note_d1?: number | null;
  absent_d1?: boolean;
  note_d2?: number | null;
  absent_d2?: boolean;
}

export interface SaisieRapideDevoirsPayload {
  matiere_id: number;
  niveau: string;
  option: string;
  semestre: 'S1' | 'S2';
  annee_academique: string;
  notes: SaisieRapideNote[];
}

export interface DoublonReleveDetail {
  id: number;
  code_releve: string;
  status: string;
  option?: string;
  classes?: string;
  enseignant_nom?: string;
  nombre_copies?: number;
  nombre_etudiants?: number;
  codes_copies_count?: number;
  codes_copies_sample?: string[];
  matricules_count?: number;
  matricules_sample?: string[];
  date_generation?: string;
  date_validation?: string | null;
}
export interface DoublonGroupItem {
  matiere_id: number;
  matiere_code: string;
  matiere_nom: string;
  niveau: string;
  semestre: string;
  annee_academique: string;
  type_examen?: string;
  nb_traites: number;
  nb_en_cours: number;
  // Comparaison entre versions traitées et en cours
  codes_communs_count?: number;
  codes_uniques_en_cours?: string[];
  codes_uniques_en_cours_count?: number;
  matricules_communs_count?: number;
  matricules_uniques_en_cours?: string[];
  matricules_uniques_en_cours_count?: number;
  releves_traites: DoublonReleveDetail[];
  releves_en_cours_a_supprimer: DoublonReleveDetail[];
}
export interface DoublonsResponse {
  count: number;
  doublons: DoublonGroupItem[];
}

export interface SaisieRapideDevoirsResponse {
  success: boolean;
  message: string;
  notes_creees: number;
  notes_modifiees: number;
  erreurs?: any[];
}

// ============================================
// Types pour le Batch Upload (QR Code)
// ============================================

export interface BatchUploadFichierAssocie {
  fichier: string;
  code_releve: string;
  type: string;
  matiere: string;
  niveau: string;
  semestre: string;
  releve_id: number;
}

export interface BatchUploadFichierNonIdentifie {
  fichier: string;
  raison: string;
  qr_data?: Record<string, string>;
}

export interface BatchUploadFichierAssocieQR extends BatchUploadFichierAssocie {
  ue?: string;
  option?: string;
  ocr_status?: string | null;
}

export interface BatchUploadResponse {
  total: number;
  associes: BatchUploadFichierAssocieQR[];
  non_identifies: BatchUploadFichierNonIdentifie[];
  nb_associes: number;
  nb_non_identifies: number;
  ocr_lance?: { releve_id: number; type: string }[];
}

// Réponse pour upload_batch_par_qr (resource-scoped, sans auto-OCR)
export type StatutFichierBatchQR =
  | 'success'
  | 'remplace'
  | 'deja_uploade'
  | 'deja_traite'
  | 'non_reconnu'
  | 'erreur';

export interface FichierBatchQRResult {
  fichier: string;
  taille?: number;
  statut: StatutFichierBatchQR;
  message: string;
  // Si le QR a permis de matcher un relevé :
  code_releve?: string;
  releve_id?: number;
  matiere_code?: string;
  matiere_nom?: string;
  niveau?: string;
  option?: string;
  semestre?: string;
  enseignant_nom?: string;
  classes?: string;          // examens uniquement
  type_examen?: string;      // examens uniquement
  statut_releve_avant?: string;
  statut_releve_apres?: string;
  code_detecte?: string;     // si QR lu mais releve introuvable
}

export interface BatchQRSummary {
  total: number;
  success: number;
  remplace: number;
  deja_uploade: number;
  deja_traite: number;
  non_reconnu: number;
  erreur: number;
}

export interface UploadBatchQRResponse {
  success: boolean;
  summary: BatchQRSummary;
  remplacer_actif: boolean;
  results: FichierBatchQRResult[];
  uploaded_by: string;
  date_upload: string;
}

// Historique des imports de scans
export interface HistoriqueImportScan {
  id: number;
  date_upload: string;
  utilisateur: number | null;
  utilisateur_nom: string;
  mode: 'single' | 'batch';
  mode_label: string;
  type_releve: 'devoirs' | 'correction';
  type_releve_label: string;
  nombre_fichiers: number;
  nombre_success: number;
  nombre_remplace: number;
  nombre_deja_uploade: number;
  nombre_deja_traite: number;
  nombre_non_reconnu: number;
  nombre_erreur: number;
  remplacer_actif: boolean;
  releve_devoirs: number | null;
  releve_correction: number | null;
  releve_code: string | null;
  releve_matiere: string | null;
  details: FichierBatchQRResult[];
}

export interface HistoriqueImportsResponse {
  count: number;
  results: HistoriqueImportScan[];
}

export interface HistoriqueImportsParams {
  type_releve?: 'devoirs' | 'correction';
  mode?: 'single' | 'batch';
  utilisateur?: number;
  date_min?: string;
  date_max?: string;
  limit?: number;
}

export interface UploadScanManuelResponse {
  success: boolean;
  message: string;
  releve_id: number;
  code_releve: string;
}

export interface LancerOCRResponse {
  success: boolean;
  message: string;
  task_id: string;
  releve_id: number;
}

export interface CopieManquante {
  code_copie: string;
  etudiant_matricule?: string;
  etudiant_nom?: string;
}

// ============================================
// API
// ============================================

export const ocrApi = createApi({
  reducerPath: 'ocrApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ReleveDevoirs', 'ReleveCorrection', 'TraitementOCR', 'ConfigurationOCR', 'StatistiquesOCR'],
  endpoints: (builder) => ({
    // ==========================================
    // RELEVÉS DE DEVOIRS
    // ==========================================

    getRelevesDevoirs: builder.query<ReleveDevoirs[], Record<string, any> | void>({
      query: (params) => ({
        url: '/ocr/releves-devoirs/',
        params: params ?? undefined,
      }),
      transformResponse: (response: { results: ReleveDevoirs[] } | ReleveDevoirs[]) => {
        // Gérer la réponse paginée ou non paginée
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: ['ReleveDevoirs'],
    }),

    getReleveDevoirsById: builder.query<ReleveDevoirsDetail, number>({
      query: (id) => `/ocr/releves-devoirs/${id}/`,
      providesTags: ['ReleveDevoirs'],
    }),

    genererReleveDevoirs: builder.mutation<GenererReleveResponse, GenererReleveDevoirsPayload>({
      query: (data) => ({
        url: '/ocr/releves-devoirs/generer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    genererBatchDevoirs: builder.mutation<GenererBatchDevoirsResponse, GenererBatchDevoirsPayload>({
      query: (data) => ({
        url: '/ocr/releves-devoirs/generer_batch/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    uploadScanDevoirs: builder.mutation<{ success: boolean; message: string; is_reupload?: boolean; ocr_status?: string; ocr_task_id?: string }, { releveId: number; formData: FormData }>({
      query: ({ releveId, formData }) => ({
        url: `/ocr/releves-devoirs/${releveId}/upload_scan/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveDevoirs', 'TraitementOCR', 'StatistiquesOCR'],
    }),

    // Upload en lot par QR (devoirs) — n'enclenche pas l'OCR
    uploadBatchQRDevoirs: builder.mutation<UploadBatchQRResponse, FormData>({
      query: (formData) => ({
        url: '/ocr/releves-devoirs/upload_batch_par_qr/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    lancerOCRDevoirs: builder.mutation<LancerOCRResponse, number>({
      query: (releveId) => ({
        url: `/ocr/releves-devoirs/${releveId}/lancer_ocr/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveDevoirs', 'TraitementOCR'],
    }),

    getLignesOCRDevoirs: builder.query<LigneReleveDevoirsOCR[], { releveId: number; aVerifier?: boolean }>({
      query: ({ releveId, aVerifier }) => ({
        url: `/ocr/releves-devoirs/${releveId}/lignes_ocr/`,
        params: aVerifier ? { a_verifier: 'true' } : undefined,
      }),
      providesTags: ['ReleveDevoirs'],
    }),

    validerLigneDevoirs: builder.mutation<{ success: boolean; message: string }, { releveId: number; data: ValiderLigneDevoirsPayload }>({
      query: ({ releveId, data }) => ({
        url: `/ocr/releves-devoirs/${releveId}/valider_ligne/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveDevoirs'],
    }),

    validerToutDevoirs: builder.mutation<{ success: boolean; message: string; task_id?: string }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-devoirs/${releveId}/valider_tout/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    // Re-migrer les notes OCR vers la BDD (apres correction post-validation)
    remigrerNotesDevoirs: builder.mutation<{
      success: boolean;
      message: string;
      releve_status: string;
      result?: { notes_creees?: number; notes_maj?: number; notes_remappees?: number; notes_protegees?: number };
    }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-devoirs/${releveId}/remigrer-notes/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    remigrerNotesCorrection: builder.mutation<{
      success: boolean;
      message: string;
      releve_status: string;
      result?: any;
    }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-correction/${releveId}/remigrer-notes/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    deleteReleveDevoirs: builder.mutation<{ success: boolean; message: string }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-devoirs/${releveId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    // Saisie rapide manuelle des devoirs (sans OCR)
    saisieRapideDevoirs: builder.mutation<SaisieRapideDevoirsResponse, SaisieRapideDevoirsPayload>({
      query: (data) => ({
        url: '/ocr/releves-devoirs/saisie-rapide/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StatistiquesOCR'],
    }),

    // ==========================================
    // RELEVÉS DE CORRECTION
    // ==========================================

    getRelevesCorrection: builder.query<ReleveCorrection[], Record<string, any> | void>({
      query: (params) => ({
        url: '/ocr/releves-correction/',
        params: params ?? undefined,
      }),
      transformResponse: (response: { results: ReleveCorrection[] } | ReleveCorrection[]) => {
        // Gérer la réponse paginée ou non paginée
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: ['ReleveCorrection'],
    }),

    getReleveCorrectionById: builder.query<ReleveCorrectionDetail, number>({
      query: (id) => `/ocr/releves-correction/${id}/`,
      providesTags: ['ReleveCorrection'],
    }),

    genererReleveCorrection: builder.mutation<GenererReleveResponse, GenererReleveCorrectionPayload>({
      query: (data) => ({
        url: '/ocr/releves-correction/generer/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    getMatieresAvecCopies: builder.query<MatiereAvecCopies[], MatiereAvecCopiesParams>({
      query: (params) => ({
        url: '/ocr/releves-correction/matieres_avec_copies/',
        params,
      }),
      providesTags: ['ReleveCorrection'],
    }),

    genererBatchCorrection: builder.mutation<GenererBatchCorrectionResponse, GenererBatchCorrectionPayload>({
      query: (data) => ({
        url: '/ocr/releves-correction/generer_batch/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    uploadScanCorrection: builder.mutation<{ success: boolean; message: string }, { releveId: number; formData: FormData }>({
      query: ({ releveId, formData }) => ({
        url: `/ocr/releves-correction/${releveId}/upload_scan/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveCorrection'],
    }),

    // Upload en lot par QR (correction/examens) — n'enclenche pas l'OCR
    uploadBatchQRCorrection: builder.mutation<UploadBatchQRResponse, FormData>({
      query: (formData) => ({
        url: '/ocr/releves-correction/upload_batch_par_qr/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    // Historique des imports de scans (unitaires + en lot)
    getHistoriqueImports: builder.query<HistoriqueImportsResponse, HistoriqueImportsParams | void>({
      query: (params) => ({
        url: '/ocr/historique-imports/',
        params: params || undefined,
      }),
      providesTags: ['ReleveDevoirs', 'ReleveCorrection'],
    }),

    lancerOCRCorrection: builder.mutation<LancerOCRResponse, number>({
      query: (releveId) => ({
        url: `/ocr/releves-correction/${releveId}/lancer_ocr/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveCorrection', 'TraitementOCR'],
    }),

    getLignesOCRCorrection: builder.query<LigneReleveCorrectionOCR[], { releveId: number; aVerifier?: boolean }>({
      query: ({ releveId, aVerifier }) => ({
        url: `/ocr/releves-correction/${releveId}/lignes_ocr/`,
        params: aVerifier ? { a_verifier: 'true' } : undefined,
      }),
      providesTags: ['ReleveCorrection'],
    }),

    validerLigneCorrection: builder.mutation<{ success: boolean; message: string }, { releveId: number; data: ValiderLigneCorrectionPayload }>({
      query: ({ releveId, data }) => ({
        url: `/ocr/releves-correction/${releveId}/valider_ligne/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveCorrection'],
    }),

    validerToutCorrection: builder.mutation<{ success: boolean; message: string; task_id?: string }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-correction/${releveId}/valider_tout/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    retournerReleveCorrection: builder.mutation<
      { success: boolean; message: string; nouveau_status: string },
      { releveId: number; supprimer_scan?: boolean; motif?: string }
    >({
      query: ({ releveId, supprimer_scan, motif }) => ({
        url: `/ocr/releves-correction/${releveId}/retourner_en_cours/`,
        method: 'POST',
        body: { supprimer_scan: !!supprimer_scan, motif: motif || '' },
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    retournerReleveDevoirs: builder.mutation<
      { success: boolean; message: string; nouveau_status: string },
      { releveId: number; supprimer_scan?: boolean; motif?: string }
    >({
      query: ({ releveId, supprimer_scan, motif }) => ({
        url: `/ocr/releves-devoirs/${releveId}/retourner_en_cours/`,
        method: 'POST',
        body: { supprimer_scan: !!supprimer_scan, motif: motif || '' },
      }),
      invalidatesTags: ['ReleveDevoirs', 'TraitementOCR', 'StatistiquesOCR'],
    }),

    // ── Solution "codes-barres illisibles" ─────────────────────────────────
    // Récupère les étudiants attendus non détectés + les lignes orphelines
    // (image OK mais matricule non lu par l'OCR).
    getEtudiantsManquantsDevoirs: builder.query<{
      total_attendus: number; total_presents: number;
      total_manquants: number; total_orphelines: number;
      manquants: Array<{ id: number; matricule: string; nom: string; prenoms: string; classe: string }>;
      lignes_orphelines: Array<{
        id: number; numero_ligne: number; numero_page: number;
        nom_detecte: string; has_image: boolean;
        note_d1_detectee: number | null; note_d2_detectee: number | null;
      }>;
    }, number>({
      query: (releveId) => `/ocr/releves-devoirs/${releveId}/etudiants-manquants/`,
      providesTags: ['ReleveDevoirs'],
    }),

    // Ajoute une nouvelle ligne pour un étudiant non détecté
    ajouterLigneManuelleDevoirs: builder.mutation<{
      success: boolean; ligne_id: number; matricule: string; etudiant_nom: string; message: string;
    }, {
      releveId: number;
      matricule: string;
      note_d1?: number | null; absent_d1?: boolean;
      note_d2?: number | null; absent_d2?: boolean;
      commentaire?: string;
    }>({
      query: ({ releveId, ...body }) => ({
        url: `/ocr/releves-devoirs/${releveId}/ajouter-ligne-manuelle/`,
        method: 'POST', body,
      }),
      invalidatesTags: ['ReleveDevoirs'],
    }),

    // Assigne un matricule à une ligne orpheline (image OK, code-barre illisible)
    assignerEtudiantLigneDevoirs: builder.mutation<{
      success: boolean; ligne_id: number; matricule: string; etudiant_nom: string; message: string;
    }, { releveId: number; ligneId: number; matricule: string }>({
      query: ({ releveId, ligneId, matricule }) => ({
        url: `/ocr/releves-devoirs/${releveId}/assigner-etudiant/`,
        method: 'POST',
        body: { ligne_id: ligneId, matricule },
      }),
      invalidatesTags: ['ReleveDevoirs'],
    }),

    supprimerLigneDevoirs: builder.mutation<
      { success: boolean; message: string; total_notes_detectees: number },
      { releveId: number; ligneId: number }
    >({
      query: ({ releveId, ligneId }) => ({
        url: `/ocr/releves-devoirs/${releveId}/supprimer_ligne/`,
        method: 'POST',
        body: { ligne_id: ligneId },
      }),
      invalidatesTags: ['ReleveDevoirs'],
    }),

    // Doublons relevés (devoirs)
    getDoublonsDevoirs: builder.query<DoublonsResponse, string | void>({
      query: (annee) => ({
        url: '/ocr/releves-devoirs/doublons-en-cours/',
        params: annee ? { annee_academique: annee } : undefined,
      }),
      providesTags: ['ReleveDevoirs'],
    }),
    supprimerDoublonsDevoirs: builder.mutation<
      { success: boolean; count: number; deleted: any[] },
      { annee_academique?: string; releve_ids?: number[] } | void
    >({
      query: (body) => ({
        url: '/ocr/releves-devoirs/supprimer-doublons-en-cours/',
        method: 'POST',
        body: body || {},
      }),
      invalidatesTags: ['ReleveDevoirs', 'StatistiquesOCR'],
    }),

    // Doublons relevés (correction/examens)
    getDoublonsCorrection: builder.query<DoublonsResponse, string | void>({
      query: (annee) => ({
        url: '/ocr/releves-correction/doublons-en-cours/',
        params: annee ? { annee_academique: annee } : undefined,
      }),
      providesTags: ['ReleveCorrection'],
    }),
    supprimerDoublonsCorrection: builder.mutation<
      { success: boolean; count: number; deleted: any[] },
      { annee_academique?: string; releve_ids?: number[] } | void
    >({
      query: (body) => ({
        url: '/ocr/releves-correction/supprimer-doublons-en-cours/',
        method: 'POST',
        body: body || {},
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),

    deleteReleveCorrection: builder.mutation<{ success: boolean; message: string }, number>({
      query: (releveId) => ({
        url: `/ocr/releves-correction/${releveId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ReleveCorrection', 'StatistiquesOCR'],
    }),


    getCopiesManquantes: builder.query<{ copies_manquantes: CopieManquante[]; total: number }, number>({
      query: (releveId) => `/ocr/releves-correction/${releveId}/copies_manquantes/`,
      providesTags: ['ReleveCorrection'],
    }),

    // Batch upload avec détection QR automatique
    batchUploadScanCorrection: builder.mutation<BatchUploadResponse, FormData>({
      query: (formData) => ({
        url: '/ocr/releves-correction/batch-upload-scan/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveCorrection', 'ReleveDevoirs', 'StatistiquesOCR'],
    }),

    // Upload batch QR unifié (examens + devoirs)
    batchUploadQR: builder.mutation<BatchUploadResponse, FormData>({
      query: (formData) => ({
        url: '/ocr/batch-upload-qr/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveCorrection', 'ReleveDevoirs', 'StatistiquesOCR'],
    }),

    // Upload manuel (fallback quand QR illisible)
    uploadScanManuel: builder.mutation<UploadScanManuelResponse, FormData>({
      query: (formData) => ({
        url: '/ocr/releves-correction/upload-scan-manuel/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ReleveCorrection'],
    }),

    // ==========================================
    // TRAITEMENTS OCR
    // ==========================================

    getTraitementsOCR: builder.query<TraitementOCR[], Record<string, any> | void>({
      query: (params) => ({
        url: '/ocr/traitements/',
        params: params ?? undefined,
      }),
      transformResponse: (response: { results: TraitementOCR[] } | TraitementOCR[]) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: ['TraitementOCR'],
    }),

    getTraitementOCRById: builder.query<TraitementOCR, number>({
      query: (id) => `/ocr/traitements/${id}/`,
      providesTags: ['TraitementOCR'],
    }),

    getProgressionTraitement: builder.query<{ status: string; progression: number; message_erreur: string }, number>({
      query: (id) => `/ocr/traitements/${id}/progression/`,
      providesTags: ['TraitementOCR'],
    }),

    // ==========================================
    // CONFIGURATION
    // ==========================================

    getConfigurations: builder.query<ConfigurationOCR[], void>({
      query: () => '/ocr/configurations/',
      transformResponse: (response: { results: ConfigurationOCR[] } | ConfigurationOCR[]) => {
        if (Array.isArray(response)) {
          return response;
        }
        return response.results || [];
      },
      providesTags: ['ConfigurationOCR'],
    }),

    getConfigurationById: builder.query<ConfigurationOCR, number>({
      query: (id) => `/ocr/configurations/${id}/`,
      providesTags: ['ConfigurationOCR'],
    }),

    creerConfiguration: builder.mutation<ConfigurationOCR, Partial<ConfigurationOCR>>({
      query: (data) => ({
        url: '/ocr/configurations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ConfigurationOCR'],
    }),

    modifierConfiguration: builder.mutation<ConfigurationOCR, { id: number; data: Partial<ConfigurationOCR> }>({
      query: ({ id, data }) => ({
        url: `/ocr/configurations/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ConfigurationOCR'],
    }),

    activerConfiguration: builder.mutation<{ success: boolean; message: string }, number>({
      query: (id) => ({
        url: `/ocr/configurations/${id}/activer/`,
        method: 'POST',
      }),
      invalidatesTags: ['ConfigurationOCR'],
    }),

    // ==========================================
    // STATISTIQUES
    // ==========================================

    getStatistiquesOCR: builder.query<StatistiquesOCR, { semestre?: string } | void>({
      query: (params) => ({
        url: '/ocr/statistiques/',
        params: params ?? undefined,
      }),
      providesTags: ['StatistiquesOCR'],
    }),

    // ==========================================
    // LEGACY ENDPOINTS (Rétrocompatibilité)
    // Ces endpoints sont pour les anciennes pages OCR
    // ==========================================

    // Traitement batch devoirs (legacy)
    traiterBatchDevoirs: builder.mutation<
      { copies_creees: number; total: number; erreurs: string[] },
      FormData
    >({
      query: (formData) => ({
        url: '/ocr/legacy/traiter-batch-devoirs/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['StatistiquesOCR'],
    }),

    // Upload copie OCR (legacy)
    uploadCopieOCR: builder.mutation<{ id: number; success: boolean }, FormData>({
      query: (formData) => ({
        url: '/ocr/legacy/upload-copie/',
        method: 'POST',
        body: formData,
      }),
    }),

    // Enregistrer résultat OCR (legacy)
    enregistrerResultatOCR: builder.mutation<
      { success: boolean },
      {
        copie_id: number;
        code_barre?: string;
        note?: number;
        confiance_code_barre: number;
        confiance_note: number;
        texte_complet?: string;
        donnees_json?: any;
      }
    >({
      query: (data) => ({
        url: '/ocr/legacy/enregistrer-resultat/',
        method: 'POST',
        body: data,
      }),
    }),

    // Valider note (legacy)
    validerNote: builder.mutation<
      { success: boolean },
      { copie_id: number; note_finale: number }
    >({
      query: (data) => ({
        url: '/ocr/legacy/valider-note/',
        method: 'POST',
        body: data,
      }),
    }),

    // Copies à valider (legacy)
    getCopiesAValider: builder.query<
      Array<{
        id: number;
        matiere_nom: string;
        note_detectee: number | null;
        confiance_note: number;
      }>,
      void
    >({
      query: () => '/ocr/legacy/copies-a-valider/',
    }),
  }),
});

export const {
  // Relevés Devoirs
  useGetRelevesDevoirsQuery,
  useGetReleveDevoirsByIdQuery,
  useGenererReleveDevoirsMutation,
  useGenererBatchDevoirsMutation,
  useUploadScanDevoirsMutation,
  useLancerOCRDevoirsMutation,
  useGetLignesOCRDevoirsQuery,
  useValiderLigneDevoirsMutation,
  useValiderToutDevoirsMutation,
  useRemigrerNotesDevoirsMutation,
  useRemigrerNotesCorrectionMutation,
  useDeleteReleveDevoirsMutation,
  useSaisieRapideDevoirsMutation,
  // Relevés Correction
  useGetRelevesCorrectionQuery,
  useGetReleveCorrectionByIdQuery,
  useGenererReleveCorrectionMutation,
  useGetMatieresAvecCopiesQuery,
  useGenererBatchCorrectionMutation,
  useUploadScanCorrectionMutation,
  useLancerOCRCorrectionMutation,
  useGetLignesOCRCorrectionQuery,
  useValiderLigneCorrectionMutation,
  useValiderToutCorrectionMutation,
  useRetournerReleveCorrectionMutation,
  useRetournerReleveDevoirsMutation,
  useSupprimerLigneDevoirsMutation,
  useGetEtudiantsManquantsDevoirsQuery,
  useAjouterLigneManuelleDevoirsMutation,
  useAssignerEtudiantLigneDevoirsMutation,
  useGetDoublonsDevoirsQuery,
  useSupprimerDoublonsDevoirsMutation,
  useGetDoublonsCorrectionQuery,
  useSupprimerDoublonsCorrectionMutation,
  useDeleteReleveCorrectionMutation,
  useGetCopiesManquantesQuery,
  // Batch Upload QR
  useBatchUploadScanCorrectionMutation,
  useBatchUploadQRMutation,
  useUploadScanManuelMutation,
  // Upload batch par QR — par ressource, sans auto-OCR
  useUploadBatchQRDevoirsMutation,
  useUploadBatchQRCorrectionMutation,
  // Historique imports
  useGetHistoriqueImportsQuery,
  // Traitements
  useGetTraitementsOCRQuery,
  useGetTraitementOCRByIdQuery,
  useGetProgressionTraitementQuery,
  // Configuration
  useGetConfigurationsQuery,
  useGetConfigurationByIdQuery,
  useCreerConfigurationMutation,
  useModifierConfigurationMutation,
  useActiverConfigurationMutation,
  // Statistiques
  useGetStatistiquesOCRQuery,
  // Legacy (rétrocompatibilité)
  useTraiterBatchDevoirsMutation,
  useUploadCopieOCRMutation,
  useEnregistrerResultatOCRMutation,
  useValiderNoteMutation,
  useGetCopiesAValiderQuery,
} = ocrApi;
